import { ResourceClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Card,
  EditPane,
  Field,
  Input,
  Select,
  Switch,
  Textarea,
  toast,
  useConfirm,
  type EditPaneTab,
} from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  CURRENCIES,
  PartSupplierClient,
  refusalMessage,
  SUPPLIER_LEAD_TIME_MODES,
  SUPPLIER_PAYMENT_TERMS,
  type SupplierPaymentTerms,
  SupplierClient,
  SupplierClosureClient,
  SupplierContactClient,
  type PartSupplierDTO,
  type SupplierClosureDTO,
  type SupplierContactDTO,
  type SupplierDTO,
  type UnitDTO,
} from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { PURCHASE_ORDER_STATUSES, PurchaseOrderClient, RECEIPT_PROGRESSES, type PurchaseOrderDTO } from "../../api/purchasing";
import { ReplenishmentClient, replenishmentKeys } from "../../api/replenishment";
import { OpenRecord } from "../../components/OpenRecord";
import { NexpimView } from "../../views";
import { HoldBadge, ProgressBadge, StatusBadge, useProgressLabel, useStatusLabel } from "../purchasing/badges";
import { paymentTermsName } from "../purchasing/paymentTerms";
import { formatCad, formatMoney, formatPrice, formatQuantity } from "../purchasing/replenishment/shared";

/** Out with the supplier and not yet closed: what the supplier still owes us. */
const OPEN_STATUSES: readonly string[] = ["AWAITING_APPROVAL", "ISSUED"];

type Translate = ReturnType<typeof useTranslation>["t"];

/**
 * What the database would refuse, said beside the field instead.
 *
 * The inputs do not enforce `required`, a negative number of days would be
 * saved as typed, and the weekday is 1 (Monday) to 7 (Sunday) or blank.
 */
export function supplierErrors(row: SupplierDTO, t: Translate): Record<string, string> | null {
  const errors: Record<string, string> = {};
  const required = t("validation.required", "Required");
  if (row.code.trim() === "") errors.code = required;
  if (row.name.trim() === "") errors.name = required;
  const notNegative = t("supplier.days.invalid", "Zero or more days, or blank for the default.");
  if (row.payment_terms === "NET_DAYS" && row.payment_terms_days != null && row.payment_terms_days < 0) errors.payment_terms_days = notNegative;
  if (row.payment_terms === "DEPOSIT" && (row.deposit_percent == null || !(row.deposit_percent > 0 && row.deposit_percent < 100))) {
    errors.deposit_percent = t("supplier.deposit.invalid", "A percentage above 0 and below 100.");
  }
  if (row.lead_time_days != null && row.lead_time_days < 0) errors.lead_time_days = notNegative;
  if (row.transit_days != null && row.transit_days < 0) errors.transit_days = notNegative;
  if (row.order_cycle_days != null && row.order_cycle_days < 1) {
    errors.order_cycle_days = t("supplier.cycle.invalid", "At least one day, or blank for the default.");
  }
  if (row.order_weekday != null && (row.order_weekday < 1 || row.order_weekday > 7)) {
    errors.order_weekday = t("supplier.weekday.invalid", "A day from Monday to Sunday, or blank.");
  }
  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * One supplier: who to call, everything ever ordered from it, and what it
 * sells us.
 *
 * <p>Opens on the purchase orders, newest first, because "what is still out
 * with them" is the question people come here with; open orders are tinted
 * and a switch narrows the list to them. The supplier record is a buffered
 * draft saved from the header, the part page's model; contacts are their own
 * resource and save row by row.</p>
 */
export function SupplierDetailPage({ supplierId }: { supplierId: number }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.SUPPLIERS);

  const suppliers = useMemo(() => new SupplierClient(http), [http]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["supplier", "detail", supplierId],
    queryFn: () => suppliers.findById(supplierId),
    enabled: allowed,
    staleTime: 60_000,
  });

  // A few hundred rows in all: fetched once, shared with nothing heavier, filtered here.
  const contactRows = useQuery({
    queryKey: ["supplier-contact", "all"],
    queryFn: () => new SupplierContactClient(http).findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });
  const contacts = useMemo(
    () =>
      (contactRows.data ?? [])
        .filter((row) => row.supplier_id === supplierId)
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)),
    [contactRows.data, supplierId],
  );

  const closures = useQuery({
    queryKey: ["supplier-closure", "supplier", supplierId],
    queryFn: async () =>
      (await new SupplierClosureClient(http).find({ page: 0, size: 500, filters: { supplier_id: [String(supplierId)] } })).content,
    enabled: allowed,
    staleTime: 60_000,
  });

  const parts = useQuery({
    queryKey: ["manufacturing", "part-supplier", "supplier", supplierId],
    queryFn: () => new PartSupplierClient(http).find({ page: 0, size: 1000, filters: { supplier_id: [String(supplierId)] } }),
    enabled: allowed,
    staleTime: 60_000,
  });

  const [draft, setDraft] = useState<SupplierDTO | null>(null);
  useEffect(() => {
    if (data) setDraft(structuredClone(data));
  }, [data]);

  const dirty = useMemo(() => Boolean(draft && data && JSON.stringify(draft) !== JSON.stringify(data)), [draft, data]);
  const errors = draft ? supplierErrors(draft, t) : null;

  const save = useMutation({
    mutationFn: (row: SupplierDTO) => suppliers.update({ ...row, code: row.code.trim(), name: row.name.trim() }),
    onSuccess: async () => {
      // The prefix: this page, the list, and every supplier picker.
      await queryClient.invalidateQueries({ queryKey: ["supplier"] });
      toast.success(t("notification.saved", "Saved"));
    },
    // The database refuses a non-subcontractor warehouse with a sentence.
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const leave = async (): Promise<boolean> => {
    if (!dirty) return true;
    return confirm({
      title: t("part.leave.title", "Discard unsaved changes?"),
      message: t("supplier.leave.message", "Your edits to this supplier have not been saved."),
      variant: "danger",
      confirmLabel: t("part.leave.discard", "Discard"),
    });
  };

  const parent = { to: "/manufacturing/suppliers", label: t("navigation.item.suppliers", "Suppliers"), beforeLeave: leave };

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.suppliers", "Suppliers")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  if (!data || !draft) {
    // findById answers null for a 404: a supplier that does not exist is not a failure.
    const missing = !isLoading && !error && data === null;
    return (
      <>
        <PageHeader parent={parent} title={t("navigation.item.suppliers", "Suppliers")} />
        {error || missing ? (
          <div className="p-4 lg:p-6">
            <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
              {missing ? t("supplier.not-found", "That supplier could not be found.") : t("error.generic", "Something went wrong. Please try again.")}
            </p>
          </div>
        ) : (
          <div className="flex h-40 items-center justify-center">
            <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        )}
      </>
    );
  }

  const orderCount = data.order_count ?? 0;
  const openCount = data.open_order_count ?? 0;

  const tabs: EditPaneTab[] = [
    {
      id: "orders",
      flush: true,
      label: t("supplier.tab.orders", "Purchase orders"),
      badge:
        openCount > 0 ? (
          <Badge variant="info" size="sm">
            {t("supplier.open-count", "{{n}} open", { n: openCount })}
          </Badge>
        ) : (
          orderCount || null
        ),
      content: <OrdersTab supplierId={supplierId} />,
    },
    {
      id: "contacts",
      flush: true,
      label: t("supplier.tab.contacts", "Contacts"),
      badge: contacts.length || null,
      content: <ContactsTab supplierId={supplierId} rows={contacts} readOnly={!canWrite} />,
    },
    {
      id: "closures",
      flush: true,
      label: t("supplier.tab.closures", "Closures"),
      badge: closures.data?.filter((row) => row.next_start_date != null).length || null,
      content: <ClosuresTab supplierId={supplierId} rows={closures.data ?? []} loading={closures.isLoading} readOnly={!canWrite} />,
    },
    {
      id: "parts",
      flush: true,
      label: t("supplier.tab.parts", "Parts"),
      badge: parts.data?.total || null,
      content: <PartsTab rows={parts.data?.content ?? []} loading={parts.isLoading} />,
    },
    {
      id: "details",
      label: t("supplier.tab.details", "Details"),
      content: (
        <DetailsTab
          saved={data}
          draft={draft}
          onChange={setDraft}
          readOnly={!canWrite}
          errors={errors}
          primary={contacts.find((row) => row.is_primary) ?? null}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        parent={parent}
        title={data.code}
        subtitle={data.name}
        badges={
          <>
            <Badge variant={data.is_active ? "success" : "error"} size="sm">
              {data.is_active ? t("status.active", "Active") : t("status.inactive", "Inactive")}
            </Badge>
            <Badge variant="neutral" size="sm">
              {data.currency}
            </Badge>
            <Badge variant="neutral" size="sm">
              {t("supplier.order-count", "{{n}} orders", { n: orderCount })}
            </Badge>
            {openCount > 0 && (
              <Badge variant="info" size="sm">
                {t("supplier.open-count", "{{n}} open", { n: openCount })}
              </Badge>
            )}
            {dirty && (
              <Badge variant="warning" size="sm">
                {t("part.unsaved", "Unsaved")}
              </Badge>
            )}
          </>
        }
        actions={
          canWrite ? (
            <>
              {dirty && (
                <Button variant="ghost" disabled={save.isPending} onClick={() => setDraft(structuredClone(data))}>
                  {t("button.discard", "Discard")}
                </Button>
              )}
              <Button
                variant="primary"
                disabled={!dirty || errors !== null}
                title={dirty && errors ? t("supplier.save.incomplete", "Fix the fields marked on the Details tab first.") : undefined}
                loading={save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {t("button.save", "Save")}
              </Button>
            </>
          ) : null
        }
      />

      <EditPane tabs={tabs} urlParam="tab" />
    </>
  );
}

/* --------------------------------------------------------------- orders --- */

/**
 * Every purchase order raised against the supplier, paged in the database.
 *
 * The supplier is a fixed filter added to whatever the grid asks for. "Open
 * only" narrows the status the same way: to the open statuses the status
 * filter also picked, or to both when it picked neither.
 */
function OrdersTab({ supplierId }: { supplierId: number }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new PurchaseOrderClient(http), [http]);
  const [openOnly, setOpenOnly] = useState(false);

  const table = useServerTable<PurchaseOrderDTO>({
    queryKey: ["purchasing", "purchase-order", "supplier", supplierId, openOnly],
    fetchPage: (request) => {
      const filters: Record<string, readonly string[]> = { ...request.filters, supplier_id: [String(supplierId)] };
      if (openOnly) {
        const picked = (filters.status ?? []).filter((status) => OPEN_STATUSES.includes(status));
        filters.status = picked.length > 0 ? picked : OPEN_STATUSES;
      }
      return orders.find({ ...request, filters });
    },
    fetchTotal: () => orders.find({ page: 0, size: 1, filters: { supplier_id: [String(supplierId)] } }).then((page) => page.total ?? 0),
  });

  const statusLabel = useStatusLabel();
  const progressLabel = useProgressLabel();
  const cols = useMemo(() => {
    const c = defineColumns<PurchaseOrderDTO>();
    const options = (values: readonly string[], label: (value: string) => string) => values.map((value) => ({ value, label: label(value) }));
    return [
      c.number("number", (row) => row.number, { headerKey: "column.po.number", filter: "range", priority: 1 }),
      c.text("order_date", (row) => row.order_date ?? "", { headerKey: "column.po.order-date", filter: "daterange", priority: 1 }),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <span className="inline-flex items-center gap-1.5">
            <StatusBadge status={row.status} />
            <HoldBadge onHold={row.is_on_hold} />
          </span>
        ),
        {
          headerKey: "column.status",
          filter: "multiselect",
          filterOptions: options(PURCHASE_ORDER_STATUSES, (value) => statusLabel(value as PurchaseOrderDTO["status"])),
          priority: 1,
        },
      ),
      c.custom("receipt_progress", (row) => row.receipt_progress ?? "", (row) => <ProgressBadge progress={row.receipt_progress} />, {
        headerKey: "column.po.progress",
        filter: "multiselect",
        filterOptions: options(RECEIPT_PROGRESSES, (value) => progressLabel(value as (typeof RECEIPT_PROGRESSES)[number])),
        priority: 2,
      }),
      c.text("required_date", (row) => row.required_date ?? "", { headerKey: "column.po.required-date", filter: "daterange", priority: 3 }),
      c.text("last_receipt_date", (row) => row.last_receipt_date ?? "", {
        headerKey: "column.po.last-receipt",
        filter: "daterange",
        defaultVisible: false,
      }),
      // In the order's own currency: grouped with cents, no symbol; the CAD column carries one.
      c.custom("subtotal", (row) => row.subtotal ?? null, (row) => <span className="tabular-nums">{formatMoney(row.subtotal)}</span>, {
        headerKey: "column.po.subtotal",
        filter: "range",
        align: "right",
        priority: 2,
      }),
      c.text("currency", (row) => row.currency, {
        headerKey: "column.currency",
        filter: "select",
        filterOptions: CURRENCIES.map((currency) => ({ value: currency, label: currency })),
        priority: 3,
      }),
      c.custom("total_cad", (row) => row.total_cad ?? null, (row) => <span className="tabular-nums">{formatCad(row.total_cad)}</span>, {
        headerKey: "column.po.total-cad",
        filter: "range",
        align: "right",
        priority: 3,
      }),
      // Not a server field: shown, but neither sorted nor filtered.
      c.number("line_count", (row) => row.line_count, { header: t("column.po.lines", "Lines"), filter: "none", enableSorting: false }),
      c.number("open_line_count", (row) => row.open_line_count, { headerKey: "column.po.open-lines", filter: "range" }),
      c.text("warehouse_code", (row) => row.warehouse_code ?? "", { headerKey: "column.stock.warehouse", filter: "none", defaultVisible: false }),
      c.text("supplier_reference", (row) => row.supplier_reference ?? "", {
        headerKey: "column.po.supplier-reference",
        defaultVisible: false,
      }),
    ];
  }, [t, statusLabel, progressLabel]);

  return (
    <DataTable<PurchaseOrderDTO>
      data={table.rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={table.loading}
      server={table.mode}
      initialSorting={[{ id: "order_date", desc: true }]}
      toolbarLead={<Switch checked={openOnly} onChange={setOpenOnly} label={t("supplier.orders.open-only", "Open only")} />}
      rowClassName={(row) => (OPEN_STATUSES.includes(row.status) ? "bg-accent-soft" : undefined)}
      rowActions={(row) => <OpenRecord target={{ kind: "purchase-order", id: row.id }} label={`#${String(row.number)}`} />}
      emptyMessage={openOnly ? t("supplier.orders.none-open", "No open orders with this supplier.") : t("supplier.orders.none", "No orders with this supplier yet.")}
    />
  );
}

/* ------------------------------------------------------------- contacts --- */

/**
 * The people at the supplier, edited in place.
 *
 * One primary per supplier, which the database holds with a unique index: a
 * row saved as primary first takes the flag off the current one, and gives it
 * back if the save is refused.
 */
function ContactsTab({ supplierId, rows, readOnly }: { supplierId: number; rows: SupplierContactDTO[]; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const contacts = useMemo(() => new SupplierContactClient(http), [http]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["supplier-contact", "all"] });
  const text = (value: string) => (value.trim() === "" ? null : value);
  const failed = (failure: unknown) => new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  const cols = useMemo(() => {
    const c = defineColumns<SupplierContactDTO>();
    return [
      c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
      c.text("role", (row) => row.role ?? "", { headerKey: "column.supplier.contact-role", priority: 2 }),
      c.custom("phone", (row) => row.phone ?? "", (row) => <PhoneLink phone={row.phone} extension={row.phone_extension} />, {
        headerKey: "column.phone",
        priority: 1,
      }),
      c.text("phone_extension", (row) => row.phone_extension ?? "", { headerKey: "column.supplier.extension", priority: 3 }),
      c.custom("email", (row) => row.email ?? "", (row) => <MailLink email={row.email} />, { headerKey: "column.email", priority: 1 }),
      c.custom(
        "is_primary",
        (row) => (row.is_primary ? "yes" : "no"),
        (row) =>
          row.is_primary ? (
            <Badge variant="info" size="sm">
              {t("supplier.contact.primary", "Primary")}
            </Badge>
          ) : (
            <span />
          ),
        {
          headerKey: "column.supplier.primary",
          filter: "select",
          filterOptions: [
            { value: "yes", label: t("common.yes", "Yes") },
            { value: "no", label: t("common.no", "No") },
          ],
        },
      ),
      c.number("position", (row) => row.position, { header: t("column.supplier.position", "Position"), filter: "none", priority: 3 }),
    ];
  }, [t]);

  return (
    <DataTable
      data={rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      editor={
        readOnly
          ? undefined
          : {
              fields: {
                name: { kind: "text", get: (row) => row.name, set: (row, value) => ({ ...row, name: value }), required: true },
                role: { kind: "text", get: (row) => row.role, set: (row, value) => ({ ...row, role: text(value) }) },
                phone: { kind: "text", get: (row) => row.phone, set: (row, value) => ({ ...row, phone: text(value) }) },
                phone_extension: {
                  kind: "text",
                  get: (row) => row.phone_extension,
                  set: (row, value) => ({ ...row, phone_extension: text(value) }),
                },
                email: { kind: "email", get: (row) => row.email, set: (row, value) => ({ ...row, email: text(value) }) },
                is_primary: { kind: "checkbox", get: (row) => row.is_primary, set: (row, value) => ({ ...row, is_primary: value }) },
                position: {
                  kind: "number",
                  get: (row) => row.position,
                  set: (row, value) => ({ ...row, position: value.trim() === "" ? 0 : Math.round(Number(value)) }),
                },
              },
              newRow: (): SupplierContactDTO => ({
                id: 0,
                supplier_id: supplierId,
                name: "",
                role: null,
                phone: null,
                phone_extension: null,
                email: null,
                is_primary: rows.length === 0,
                position: rows.reduce((max, row) => Math.max(max, row.position), 0) + 10,
              }),
              validate: (row) => (row.name.trim() === "" ? { name: t("validation.required", "Required") } : null),
              onSave: async (row, isNew) => {
                const demoted = row.is_primary ? rows.filter((other) => other.is_primary && other.id !== row.id) : [];
                try {
                  for (const other of demoted) await contacts.update({ ...other, is_primary: false });
                  const trimmed = { ...row, name: row.name.trim() };
                  if (isNew) await contacts.create(trimmed);
                  else await contacts.update(trimmed);
                } catch (failure) {
                  for (const other of demoted) await contacts.update(other).catch(() => undefined);
                  await refresh();
                  throw failed(failure);
                }
                await refresh();
                toast.success(t("notification.saved", "Saved"));
              },
              onDelete: async (row) => {
                try {
                  await contacts.remove(row.id);
                } catch (failure) {
                  throw failed(failure);
                }
                await refresh();
                toast.success(t("notification.deleted", "Deleted"));
              },
              deleteConfirm: () => ({ title: t("config.delete.title", "Delete this entry?") }),
              addLabel: t("button.add-contact", "Add contact"),
            }
      }
      emptyMessage={t("supplier.contacts.empty", "No contacts recorded for this supplier.")}
    />
  );
}

/* ------------------------------------------------------------- closures --- */

/**
 * The days the supplier takes no orders and ships nothing. Replenishment
 * plans around them: an order placed during one is taken when it reopens, a
 * shipment due during one leaves when it reopens (goods already on the road
 * keep going), and a buyer is told to order before one comes. A yearly
 * closure repeats on the same days every year.
 */
function ClosuresTab({
  supplierId,
  rows,
  loading,
  readOnly,
}: {
  supplierId: number;
  rows: SupplierClosureDTO[];
  loading: boolean;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const closures = useMemo(() => new SupplierClosureClient(http), [http]);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["supplier-closure", "supplier", supplierId] });
    // Arrival dates move with closures: the next run plans around them.
    await queryClient.invalidateQueries({ queryKey: replenishmentKeys.all });
  };
  const failed = (failure: unknown) => new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const isDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.next_start_date ?? "9999").localeCompare(b.next_start_date ?? "9999") || a.start_date.localeCompare(b.start_date)),
    [rows],
  );

  const cols = useMemo(() => {
    const c = defineColumns<SupplierClosureDTO>();
    return [
      c.text("reason", (row) => row.reason, { header: t("supplier.closure.reason", "Why"), priority: 1 }),
      c.text("start_date", (row) => row.start_date, { header: t("supplier.closure.start", "First closed day"), priority: 1 }),
      c.text("end_date", (row) => row.end_date, { header: t("supplier.closure.end", "Last closed day"), priority: 1 }),
      c.custom(
        "is_yearly",
        (row) => (row.is_yearly ? "yes" : "no"),
        (row) =>
          row.is_yearly ? (
            <Badge variant="info" size="sm">
              {t("supplier.closure.yearly", "Every year")}
            </Badge>
          ) : (
            <span />
          ),
        { header: t("supplier.closure.repeats", "Repeats"), filter: "none", priority: 2 },
      ),
      c.custom(
        "next",
        (row) => row.next_start_date ?? "",
        (row) =>
          row.next_start_date ? (
            <span className="tabular-nums">
              {row.next_start_date} → {row.next_end_date}
            </span>
          ) : (
            <span className="text-ink-faint">{t("supplier.closure.past", "over")}</span>
          ),
        { header: t("supplier.closure.next", "Next"), filter: "none", priority: 2 },
      ),
    ];
  }, [t]);

  return (
    <div className="flex flex-col">
      <p className="border-b border-line px-4 py-2 text-[12px] text-ink-muted">
        {t(
          "supplier.closures.intro",
          "While closed the supplier takes no orders and ships nothing; goods already shipped keep travelling. Replenishment orders ahead of a closure and dates orders after it.",
        )}
      </p>
      <DataTable
        data={sorted}
        columns={cols}
        loading={loading}
        getRowId={(row) => String(row.id)}
        editor={
          readOnly
            ? undefined
            : {
                fields: {
                  reason: { kind: "text", get: (row) => row.reason, set: (row, value) => ({ ...row, reason: value }), required: true },
                  start_date: {
                    kind: "text",
                    get: (row) => row.start_date,
                    set: (row, value) => ({ ...row, start_date: value.trim() }),
                    placeholder: "YYYY-MM-DD",
                    required: true,
                  },
                  end_date: {
                    kind: "text",
                    get: (row) => row.end_date,
                    set: (row, value) => ({ ...row, end_date: value.trim() }),
                    placeholder: "YYYY-MM-DD",
                    required: true,
                  },
                  is_yearly: { kind: "checkbox", get: (row) => row.is_yearly, set: (row, value) => ({ ...row, is_yearly: value }) },
                },
                newRow: (): SupplierClosureDTO => ({ id: 0, supplier_id: supplierId, start_date: "", end_date: "", reason: "", is_yearly: false }),
                validate: (row) => {
                  const errors: Record<string, string> = {};
                  if (row.reason.trim() === "") errors.reason = t("validation.required", "Required");
                  if (!isDay(row.start_date)) errors.start_date = t("validation.date", "A date as YYYY-MM-DD.");
                  if (!isDay(row.end_date)) errors.end_date = t("validation.date", "A date as YYYY-MM-DD.");
                  else if (isDay(row.start_date) && row.end_date < row.start_date) {
                    errors.end_date = t("supplier.closure.end-before-start", "Cannot come before the first day.");
                  }
                  return Object.keys(errors).length > 0 ? errors : null;
                },
                onSave: async (row, isNew) => {
                  const trimmed = { ...row, reason: row.reason.trim() };
                  try {
                    if (isNew) await closures.create(trimmed);
                    else await closures.update(trimmed);
                  } catch (failure) {
                    throw failed(failure);
                  }
                  await refresh();
                  toast.success(t("notification.saved", "Saved"));
                },
                onDelete: async (row) => {
                  try {
                    await closures.remove(row.id);
                  } catch (failure) {
                    throw failed(failure);
                  }
                  await refresh();
                  toast.success(t("notification.deleted", "Deleted"));
                },
                deleteConfirm: () => ({ title: t("config.delete.title", "Delete this entry?") }),
                addLabel: t("button.add-closure", "Add closure"),
              }
        }
        emptyMessage={t("supplier.closures.empty", "No closures recorded: the supplier is planned as always open.")}
      />
    </div>
  );
}

/** A number that dials. The click stays on the link, so it does not open the row editor. */
function PhoneLink({ phone, extension }: { phone: string | null; extension?: string | null }) {
  if (!phone) return <span />;
  const digits = extension ? extension.replace(/\D/g, "") : "";
  const dial = phone.replace(/[^\d+]/g, "") + (digits ? `,${digits}` : "");
  return (
    <a href={`tel:${dial}`} className="text-accent hover:underline" onClick={(event) => event.stopPropagation()}>
      {phone}
    </a>
  );
}

function MailLink({ email }: { email: string | null }) {
  if (!email) return <span />;
  return (
    <a href={`mailto:${email}`} className="text-accent hover:underline" onClick={(event) => event.stopPropagation()}>
      {email}
    </a>
  );
}

/* ---------------------------------------------------------------- parts --- */

/** What the supplier sells us: its part links, read here and edited on each part's Suppliers tab. */
function PartsTab({ rows, loading }: { rows: PartSupplierDTO[]; loading: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const units = useQuery({
    queryKey: ["unit", "all"],
    queryFn: () => new ResourceClient<UnitDTO>(http, "/unit").findAll(),
    staleTime: 30 * 60_000,
  });
  const unitCode = useMemo(() => new Map((units.data ?? []).map((unit) => [unit.id, unit.code])), [units.data]);

  const cols = useMemo(() => {
    const c = defineColumns<PartSupplierDTO>();
    return [
      c.custom(
        "part_number",
        (row) => row.part_number ?? "",
        (row) => (
          <Link
            to="/manufacturing/parts/$partId"
            params={{ partId: String(row.part_id) }}
            search={{ tab: "suppliers" }}
            className="text-accent hover:underline"
          >
            {row.part_number}
          </Link>
        ),
        { headerKey: "column.part.number", filter: "text", priority: 1 },
      ),
      c.text("part_name", (row) => row.part_name ?? "", { headerKey: "column.part.name", priority: 1 }),
      c.number("priority", (row) => row.priority, { headerKey: "column.supplier.priority", filter: "range", priority: 2 }),
      c.text("supplier_part_number", (row) => row.supplier_part_number ?? "", { headerKey: "column.supplier.part-no", priority: 2 }),
      c.custom("price", (row) => row.price, (row) => <span className="tabular-nums">{formatPrice(row.price)}</span>, {
        headerKey: "column.price",
        filter: "range",
        align: "right",
        priority: 1,
      }),
      c.text("currency", (row) => row.currency, { headerKey: "column.currency", filter: "select", priority: 3 }),
      c.text("unit_id", (row) => (row.unit_id ? (unitCode.get(row.unit_id) ?? "") : ""), {
        headerKey: "column.supplier.unit",
        filter: "select",
        priority: 3,
      }),
      c.custom(
        "lead_time_days",
        (row) => row.lead_time_days,
        (row) =>
          row.lead_time_days === null ? (
            <span className="text-ink-faint">{t("part-supplier.lead-time.placeholder", "Supplier's")}</span>
          ) : (
            <span className="tabular-nums">{t("supplier.days", "{{n}} d", { n: row.lead_time_days })}</span>
          ),
        { headerKey: "column.supplier.lead-time", filter: "range", priority: 3 },
      ),
      c.custom(
        "minimum_order_quantity",
        (row) => row.minimum_order_quantity,
        (row) => <span className="tabular-nums">{formatQuantity(row.minimum_order_quantity)}</span>,
        { headerKey: "column.supplier.moq", filter: "range", align: "right" },
      ),
      c.custom("order_multiple", (row) => row.order_multiple, (row) => <span className="tabular-nums">{formatQuantity(row.order_multiple)}</span>, {
        headerKey: "column.supplier.order-multiple",
        filter: "range",
        align: "right",
      }),
      c.text("price_date", (row) => row.price_date ?? "", { headerKey: "column.supplier.price-date", filter: "daterange" }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", defaultVisible: false }),
    ];
  }, [t, unitCode]);

  return (
    <DataTable
      data={rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={loading}
      initialSorting={[{ id: "part_number", desc: false }]}
      rowActions={(row) => <OpenRecord target={{ kind: "part", id: row.part_id, tab: "suppliers" }} label={row.part_number ?? ""} />}
      emptyMessage={t("supplier.parts.empty", "No parts are linked to this supplier.")}
    />
  );
}

/* -------------------------------------------------------------- details --- */

/**
 * The supplier's own fields, under a card of how to reach them. The card reads
 * the saved record, so a half-typed phone number is not offered as a link.
 */
function DetailsTab({
  saved,
  draft,
  onChange,
  readOnly,
  errors,
  primary,
}: {
  saved: SupplierDTO;
  draft: SupplierDTO;
  onChange: (next: SupplierDTO) => void;
  readOnly: boolean;
  errors: Record<string, string> | null;
  primary: SupplierContactDTO | null;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  const subcontractors = (warehouses.data ?? []).filter((w) => w.kind === "SUBCONTRACTOR" || w.id === draft.subcontract_warehouse_id);

  // What the receipts say, beside what was typed.
  const leadTimes = useQuery({
    queryKey: replenishmentKeys.reviewLeadTimes,
    queryFn: () => new ReplenishmentClient(http).leadTimeReview(),
    staleTime: 10 * 60_000,
  });
  const measured = (leadTimes.data ?? []).find((row) => row.supplier_id === saved.id);

  const weekdays = [
    t("weekday.1", "Monday"),
    t("weekday.2", "Tuesday"),
    t("weekday.3", "Wednesday"),
    t("weekday.4", "Thursday"),
    t("weekday.5", "Friday"),
    t("weekday.6", "Saturday"),
    t("weekday.7", "Sunday"),
  ];

  const text = (value: string) => (value.trim() === "" ? null : value);
  /** Blank is "use the default", not zero. */
  const whole = (value: string) => (value.trim() === "" ? null : Math.round(Number(value)));
  const error = (field: string) => (readOnly ? undefined : errors?.[field]);
  const set = <K extends keyof SupplierDTO>(key: K, value: SupplierDTO[K]) => onChange({ ...draft, [key]: value });

  type TextKey = "legal_name" | "address_line" | "city" | "postal_code" | "country" | "phone" | "fax" | "website";
  const textField = (key: TextKey, label: string) => (
    <Field label={label} htmlFor={`supplier-${key}`}>
      <Input id={`supplier-${key}`} value={draft[key] ?? ""} disabled={readOnly} onChange={(event) => set(key, text(event.target.value))} />
    </Field>
  );
  const dayField = (key: "payment_terms_days" | "lead_time_days" | "transit_days" | "order_cycle_days", label: string, hint?: string) => (
    <Field label={label} htmlFor={`supplier-${key}`} error={error(key)} hint={hint}>
      <Input
        id={`supplier-${key}`}
        type="number"
        step={1}
        min={key === "order_cycle_days" ? 1 : 0}
        value={draft[key] === null ? "" : String(draft[key])}
        placeholder={t("supplier.default", "Default")}
        disabled={readOnly}
        onChange={(event) => set(key, whole(event.target.value))}
      />
    </Field>
  );

  const website = saved.website ? (/^https?:\/\//i.test(saved.website) ? saved.website : `https://${saved.website}`) : null;
  const address = [saved.address_line, saved.city, saved.postal_code, saved.country].filter(Boolean).join(", ");

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-4 sm:p-5">
        <h3 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-ink-faint">{t("supplier.reach", "Reach them")}</h3>
        <dl className="grid gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
          <Fact label={t("supplier.primary-contact", "Primary contact")}>
            {primary ? (
              <span className="inline-flex flex-wrap items-baseline justify-end gap-x-2">
                <span>{primary.role ? `${primary.name} · ${primary.role}` : primary.name}</span>
                {primary.phone && <PhoneLink phone={primary.phone} extension={primary.phone_extension} />}
                {primary.email && <MailLink email={primary.email} />}
              </span>
            ) : null}
          </Fact>
          <Fact label={t("column.phone", "Phone")}>{saved.phone ? <PhoneLink phone={saved.phone} /> : null}</Fact>
          <Fact label={t("column.supplier.fax", "Fax")}>{saved.fax || null}</Fact>
          <Fact label={t("column.supplier.website", "Website")}>
            {website ? (
              <a href={website} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                {saved.website}
              </a>
            ) : null}
          </Fact>
          <Fact label={t("column.address", "Address")}>{address || null}</Fact>
          <Fact label={t("column.supplier.last-order", "Last order")}>{saved.last_order_date ?? null}</Fact>
        </dl>
      </Card>

      <Card className="p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t("column.supplier.code", "Code")} htmlFor="supplier-code" required={!readOnly} error={error("code")}>
            <Input id="supplier-code" value={draft.code} disabled={readOnly} onChange={(event) => set("code", event.target.value)} />
          </Field>
          <Field label={t("column.name", "Name")} htmlFor="supplier-name" required={!readOnly} error={error("name")}>
            <Input id="supplier-name" value={draft.name} disabled={readOnly} onChange={(event) => set("name", event.target.value)} />
          </Field>
          {textField("legal_name", t("column.supplier.legal-name", "Legal name"))}
          {textField("address_line", t("column.address", "Address"))}
          {textField("city", t("column.city", "City"))}
          {textField("postal_code", t("column.postal-code", "Postal code"))}
          {textField("country", t("column.country", "Country"))}
          {textField("phone", t("column.phone", "Phone"))}
          {textField("fax", t("column.supplier.fax", "Fax"))}
          {textField("website", t("column.supplier.website", "Website"))}
          <Field label={t("column.currency", "Currency")} htmlFor="supplier-currency">
            <Select
              id="supplier-currency"
              value={draft.currency}
              disabled={readOnly}
              options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
              onChange={(event) => set("currency", event.target.value as SupplierDTO["currency"])}
            />
          </Field>
          <Field label={t("column.supplier.payment-terms", "Payment terms")} htmlFor="supplier-payment-terms">
            <Select
              id="supplier-payment-terms"
              value={draft.payment_terms ?? "NET_DAYS"}
              disabled={readOnly}
              options={SUPPLIER_PAYMENT_TERMS.map((terms) => ({ value: terms, label: paymentTermsName(terms, t) }))}
              onChange={(event) => {
                const terms = event.target.value as SupplierPaymentTerms;
                // Days and the deposit only mean something under their own terms.
                onChange({
                  ...draft,
                  payment_terms: terms,
                  payment_terms_days: terms === "NET_DAYS" ? draft.payment_terms_days : null,
                  deposit_percent: terms === "DEPOSIT" ? draft.deposit_percent : null,
                });
              }}
            />
          </Field>
          {draft.payment_terms === "NET_DAYS" &&
            dayField("payment_terms_days", t("column.supplier.payment-terms-days", "Terms (d)"), t("supplier.payment-terms.net.hint", "Days after receipt. Blank: the purchasing default."))}
          {draft.payment_terms === "DEPOSIT" && (
            <Field
              label={t("column.supplier.deposit", "Deposit (%)")}
              htmlFor="supplier-deposit"
              required={!readOnly}
              error={error("deposit_percent")}
              hint={t("supplier.deposit.hint", "Paid when the order is sent; the rest is paid on receipt.")}
            >
              <Input
                id="supplier-deposit"
                type="number"
                step={1}
                min={1}
                max={99}
                value={draft.deposit_percent == null ? "" : String(draft.deposit_percent)}
                disabled={readOnly}
                onChange={(event) => set("deposit_percent", event.target.value.trim() === "" ? null : Number(event.target.value))}
              />
            </Field>
          )}
          <Field label={t("column.supplier.lead-mode", "Lead time from")} htmlFor="supplier-lead-mode">
            <Select
              id="supplier-lead-mode"
              value={draft.lead_time_mode}
              disabled={readOnly}
              options={SUPPLIER_LEAD_TIME_MODES.map((mode) => ({
                value: mode,
                label: mode === "MEASURED" ? t("supplier.lead-mode.measured", "Measured") : t("supplier.lead-mode.fixed", "Fixed"),
              }))}
              onChange={(event) => set("lead_time_mode", event.target.value as SupplierDTO["lead_time_mode"])}
            />
          </Field>
          {dayField(
            "lead_time_days",
            t("column.supplier.lead-time", "Lead time"),
            measured?.measured_days
              ? t("supplier.measured", "measured {{days}} d ({{count}})", { days: measured.measured_days, count: measured.receipt_count })
              : undefined,
          )}
          {dayField(
            "transit_days",
            t("column.supplier.transit", "Transit (d)"),
            t("supplier.transit.hint", "Days on the road once shipped: a closure does not stop these. Blank: all of the lead time is the supplier's."),
          )}
          {dayField("order_cycle_days", t("column.supplier.order-cycle", "Order cycle (d)"))}
          <Field label={t("column.supplier.order-day", "Order day")} htmlFor="supplier-weekday" error={error("order_weekday")}>
            <Select
              id="supplier-weekday"
              value={draft.order_weekday ? String(draft.order_weekday) : ""}
              disabled={readOnly}
              placeholder={t("supplier.default", "Default")}
              options={weekdays.map((label, index) => ({ value: String(index + 1), label }))}
              onChange={(event) => set("order_weekday", event.target.value === "" ? null : Number(event.target.value))}
            />
          </Field>
          <Field label={t("column.supplier.subcontract-warehouse", "Subcontract warehouse")} htmlFor="supplier-warehouse">
            <Select
              id="supplier-warehouse"
              value={draft.subcontract_warehouse_id ? String(draft.subcontract_warehouse_id) : ""}
              disabled={readOnly}
              placeholder={t("supplier.none", "None")}
              options={subcontractors.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
              onChange={(event) => set("subcontract_warehouse_id", event.target.value === "" ? null : Number(event.target.value))}
            />
          </Field>
          <Field label={t("form.field.note", "Note")} htmlFor="supplier-note" className="sm:col-span-2 lg:col-span-3">
            <Textarea id="supplier-note" rows={3} value={draft.note ?? ""} disabled={readOnly} onChange={(event) => set("note", text(event.target.value))} />
          </Field>
          <div className="sm:col-span-2 lg:col-span-3">
            <Switch checked={draft.is_active} disabled={readOnly} onChange={(value) => set("is_active", value)} label={t("status.active", "Active")} />
          </div>
        </div>
      </Card>
    </div>
  );
}

/** One line of the contact card. A blank value shows a dot, so the card keeps its shape. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line-soft pb-1.5">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd className="min-w-0 text-right font-medium text-ink">{children ?? <span className="text-ink-faint">·</span>}</dd>
    </div>
  );
}
